<template>
  <div class="app-body_show container-fluid pt-4 pb-5">
    <div class="row">
      <div class="col-md-3">
        <scientific-awards
          v-if="isLoaded && !isEmpty(researcher.scientific_awards)"
          :awards="researcher.scientific_awards"
          class="card mb-4"
        />

        <flex-chart
          v-if="!isGraphLoaded || !(isGraphLoaded && !graphData.publications)"
          :isLoaded="!!(isGraphLoaded && graphData.publications)"
          :graphData="parsedGraphData.publications || []"
          :legend="parsedGraphData.publicationsLegend || {}"
          :name="'Publications'"
          :scope="'researchers'"
          :chartType="'bar'"
          :color="'#43AEC5'"
          isSmall
          withContainer>
        </flex-chart>

        <flex-chart
          v-if="!isGraphLoaded || !(isGraphLoaded && !graphData.citations)"
          :isLoaded="!!(isGraphLoaded && graphData.citations)"
          :graphData="parsedGraphData.citations || []"
          :legend="parsedGraphData.citationsLegend || {}"
          :name="'Citations'"
          :scope="'researchers'"
          :chartType="'line'"
          :color="'#EFA9BB'"
          isSmall
          withContainer>
        </flex-chart>

        <!-- render collaboration-interest -->
        <collaboration-interest v-if="canShowComponent(researcher.collaboration_interests)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </collaboration-interest>

        <!-- render research-interest -->
        <research-interest v-if="canShowComponent(researcher.research_interests) || canShowComponent(researcher.keywords)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </research-interest>

        <!-- render discipline -->
        <discipline v-if="canShowComponent(researcher.disciplines)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </discipline>

        <!-- render sector -->
        <sector v-if="canShowComponent(researcher.sectors)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </sector>

        <!-- render therapeutics -->
        <therapeutic v-if="canShowComponent(researcher.therapeutics)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </therapeutic>

        <!-- render medical technologies -->
        <medical-technology v-if="canShowComponent(researcher.medical_technologies)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </medical-technology>

        <!-- render research-type -->
        <research-type v-if="canShowComponent(researcher.research_types)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </research-type>

        <!-- render modal organisms -->
        <model-organism v-if="canShowComponent(researcher.model_organisms)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </model-organism>

        <!-- render diseases -->
        <disease v-if="canShowComponent(researcher.diseases)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </disease>

        <search-related-documents v-if="isLoaded"
          :filter="searchFilterObject"
          :parent="researcher"
          :routeName="'researcher'"
          :indexes="['researchers']">
        </search-related-documents>
      </div>

      <div class="col-md-6">
        <bar-search-categories
          :indexes="indexes"
          :routeName="'researcher.explore'"
          :isSticky="false"
          :indexCount="4"
          :showEmptyBar="false"
        />
        <search-bar
          v-if="isLoaded && hasResearch"
          class="mb-4"
          :state="searchFilterObject"
          :indexes="indexes"
        />
        <!-- render biography -->
        <biography v-if="canShowComponent(researcher.about)"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :canEdit="canEdit"
          class="card mb-4">
        </biography>

        <tree-chart
          :isLoaded="isTreeGraphLoaded"
          :graphData="treeGraphData"
          :parent="{
            label: researcher.full_name,
            value: researcher.slug_text
          }"
          :title="'Top co-authoring organisations'"
          :type="'organisation'"
          :exploreRoute="exploreRoute"
        />

        <search-container-preview v-if="isLoaded"
          :searchFilterObject="searchFilterObject"
          :indexes="['projects', 'publications', 'clinical_trials', 'patents']"
          :suggestionIndexes="['projects', 'publications', 'clinical_trials', 'patents']"
          :route="{ name: 'researcher', slug: researcher.slug_values[0]}"
          :scope="'researchers'">
        </search-container-preview>

        <!-- render experience && education -->
        <div v-if="canShowComponent(researcher.experiences) || canShowComponent(researcher.educations)" class="card">
          <experience v-if="canShowComponent(researcher.experiences)"
            :isLoaded="isLoaded"
            :researcher="researcher"
            :canEdit="canEdit">
          </experience>

          <education v-if="canShowComponent(researcher.educations)"
            :isLoaded="isLoaded"
            :researcher="researcher"
            :canEdit="canEdit">
          </education>
        </div>
      </div>

      <div class="col-md-3">
        <!-- pending organisation request -->
        <pending-invite-request v-if="isLoaded"
          :user="user"
          :checkCookie="true"
          :isClose="true">
        </pending-invite-request>

        <!-- pending organisation request -->
        <pending-organisation-request v-if="isLoaded"
          :user="user"
          :checkCookie="true"
          :isClose="true">
        </pending-organisation-request>

        <!-- account completion -->
        <account-completion v-if="canEdit && isLoaded"
          :isLoaded="isLoaded"
          :researcher="researcher"
          :user="user"
          @openAvatar="openAvatar"
          @uploadAvatar="uploadAvatar">
        </account-completion>

        <profile-actions
          scope="researchers"
          :item="researcher"
          :contacts="contacts"
          :isLoaded="isLoaded"
        />

        <!-- render contact-information -->
        <contact :isLoaded="isLoaded"
          :item="researcher"
          :user="user"
          :scope="`researchers`"
          :contacts="contacts"
          :canEdit="canEdit"
        />

        <active-email-address
          v-if="isLoaded && $currentUser"
          :emailsObject="researcher.primary_emails"
        />

        <identifiers :item="researcher" />
      </div>
    </div>
  </div>
</template>

<script>
// import Spinner from '@/components/shared/Spinner'
// import Timeline from '@/components/shared/twitter/Timeline'

import ResearchInterest from '@/components/researcher/show/ResearchInterest'
import ModelOrganism from '@/components/researcher/show/ModelOrganism'
import Disease from '@/components/researcher/show/Disease'
import ResearchType from '@/components/researcher/show/ResearchType'
import Therapeutic from '@/components/researcher/show/Therapeutic'
import MedicalTechnology from '@/components/researcher/show/MedicalTechnology'
import Discipline from '@/components/researcher/show/Discipline'
import CollaborationInterest from '@/components/researcher/show/CollaborationInterest'
import Sector from '@/components/researcher/show/Sector'
import Biography from '@/components/researcher/show/Biography'
import Experience from '@/components/researcher/show/Experience'
import Education from '@/components/researcher/show/Education'
import Contact from '@/components/contact/Show'
import SearchContainerPreview from '@/containers/SearchPreview'
import SearchRelatedDocuments from '@/containers/SearchRelatedDocuments'
import PendingOrganisationRequest from '@/components/organisation_request/Pending'
import PendingInviteRequest from '@/components/invite/Pending'
import SearchBar from '@/components/search/BarDashboard'
import ScientificAwards from '@/components/researcher/show/ScientificAwards'
import TreeChart from '@/components/shared/chart/Tree'
import withTree from '@/components/shared/chart/withTree'
import FlexChart from '@/components/shared/chart/FlexChart'
import BarSearchCategories from '@/components/search/BarSearchCategories'
import Identifiers from '@/components/qa/Identifiers'
import ProfileActions from '@/components/shared/profile/Actions'
import ActiveEmailAddress from '@/components/shared/ActiveEmailAddress'

// TODO: Need this to be a card, or in researcher/show?
import AccountCompletion from '@/components/user/AccountCompletion'
// TODO: Where do we want to store a user pending invites helper?

import SearchService from '@/services/search'
import searchHelpers from '@/mixins/search/helpers'
import ResearcherService from '@/services/researcher'

export default {
  name: 'researcherShow',
  mixins: [searchHelpers, withTree],
  components: {
    ResearchInterest,
    ModelOrganism,
    AccountCompletion,
    Disease,
    CollaborationInterest,
    ResearchType,
    Discipline,
    Sector,
    Therapeutic,
    MedicalTechnology,
    Experience,
    Education,
    Contact,
    Biography,
    PendingOrganisationRequest,
    PendingInviteRequest,
    SearchRelatedDocuments,
    SearchContainerPreview,
    SearchBar,
    ScientificAwards,
    TreeChart,
    FlexChart,
    BarSearchCategories,
    Identifiers,
    ProfileActions,
    ActiveEmailAddress
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      required: true
    },
    researcher: {
      type: Object,
      required: true
    },
    searchFilterObject: {
      type: Object,
      required: true
    },
    hasResearch: {
      type: Boolean,
      required: true
    },
    indexes: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      keywords: [],
      isGraphLoaded: false,
      graphData: {},
      contacts: []
    }
  },
  computed: {
    canEdit () {
      return this.user ? this.$can.edit('researcher', this.user.rw_access) : false
    },
    parsedGraphData () {
      if (!this.graphData || this.isEmpty(this.graphData)) {
        return {}
      }
      const pubs = this.formatGraphData(this.graphData.publications)
      const cits = this.formatGraphData(this.graphData.citations)
      const firstPub = pubs.findIndex(p => p.y > 0)
      const firstCit = cits.findIndex(p => p.y > 0)
      const earliestIndex = firstPub < firstCit ? firstPub : firstCit

      return {
        publicationsLegend: this.getTotals(this.graphData.publications),
        citationsLegend: this.getTotals(this.graphData.citations),
        publications: pubs.slice(Math.max(earliestIndex, 0)),
        citations: cits.slice(Math.max(earliestIndex, 0))
      }
    }
  },
  mounted () {
    if (this.isLoaded) {
      this.fetchGraphData()
      this.fetchTreeGraphData(
        this.getSearchObjectGraphData(this.searchFilterObject, ['publications']),
        this.researcher.organisations ? this.researcher.organisations.map(org => org.slug_text) : []
      )
      this.fetchContactInfo()
    }
  },
  watch: {
    isLoaded (newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.fetchGraphData()
        this.fetchTreeGraphData(
          this.getSearchObjectGraphData(this.searchFilterObject, ['publications']),
          this.researcher.organisations ? this.researcher.organisations.map(org => org.slug_text) : []
        )
        this.fetchContactInfo()
      }
    }
  },
  methods: {
    fetchGraphData () {
      this.graphData = {}
      this.isGraphLoaded = false
      const searchObject = this.getSearchObjectGraphData(this.searchFilterObject, ['publications'])
      SearchService.graphData(searchObject).then(graphData => {
        this.graphData = graphData
        this.isGraphLoaded = true
      }).catch(err => {
        this.error = this.handleError(err)
        this.isGraphLoaded = true
      })
    },
    canShowComponent (data) {
      return !!((!this.isLoaded || this.canEdit || !this.isEmpty(data)))
    },
    canShowComponentOnCount (data) {
      return !!((!this.isLoaded || this.canEdit || data > 0))
    },
    openAvatar (url) {
      this.$events.$emit('user:avatar:open', url)
    },
    uploadAvatar (e) {
      this.$events.$emit('user:avatar:upload', e)
    },
    getTotals (data) {
      let total = 0
      let suggestions = 0
      data.forEach(item => {
        total += item.total
        suggestions += item.total_suggestions
      })
      return {
        total,
        suggestions
      }
    },
    formatGraphData (data) {
      if (!data) {
        return []
      }
      const items = data
        .sort((a, b) => b.year - a.year)
        .reduce((a, b, i, arr) => {
          // If there is no year, or it's super long ago just go to the next value
          if (!b.year || b.year < 1970) return a

          const prevYear = i && arr[i - 1].year
          // Check if there are any gaps between years, add missing dataPoints if needed
          if (prevYear && prevYear < b.year - 1) {
            const count = b.year - prevYear - 1
            for (let j = 0; j < count; j++) {
              a.push({
                x: prevYear + j + 1,
                y: 0
              })
            }
          }
          // Include suggestion counts into the graph data
          a.push({
            x: b.year,
            y: b.count
          })
          return a
        }, [])

      return items.map(i => {
        return {
          ...i,
          year: i.x.toString()
        }
      })
    },
    exploreRoute (bucket) {
      return {
        name: `researcher.explore`,
        params: {
          category: this.searchFilterObject.index,
          buckets: [`organisations:${bucket}`]
        }
      }
    },
    fetchContactInfo () {
      this.contacts = []
      ResearcherService.getContactInfo(this.researcher.slug_values[0]).then(contact => {
        // Check which URLs is linkedIn URL.
        // Check which other URL is relevant (the first non-linked in URL)
        // Drop other URLs because reasons...
        let hasLinkedInUrl = false
        const newContactInfoArray = []
        if (contact.contact_info) {
          let addedRegularUrl = false

          contact.contact_info.forEach(el => {
            if (el.contact_type.slug !== 'url') {
              newContactInfoArray.push(el)
            } else if (el.value.indexOf('linkedin.com') > -1) {
              newContactInfoArray.push({
                ...el,
                isLinkedIn: true
              })
              hasLinkedInUrl = true
            } else if (!addedRegularUrl) {
              newContactInfoArray.push(el)
              addedRegularUrl = true
            }
          })
        }
        if (!hasLinkedInUrl) {
          // If no LI url, try to search LI by the person's name and current affiliations
          const presentOrgs = this.researcher.highlights.organisations
          var presentOrgsQuery = ''
          if (presentOrgs) {
            presentOrgsQuery = presentOrgs.map(org => `"${org.name}"`).join(' OR ')
            presentOrgsQuery = ` AND (${presentOrgsQuery})`
          }

          newContactInfoArray.push({
            contact_type: {
              slug: 'linkedin_suggestion'
            },
            value: `https://www.linkedin.com/search/results/people/?keywords=${this.researcher.full_name}${presentOrgsQuery}&origin=FACETED_SEARCH`
          })
        }
        contact.contact_info = newContactInfoArray

        this.contacts.push(contact)
      }).catch(() => {
      })
    }
  }
}
</script>
